import React from 'react'
import { RectangleButton, RectangleButtonContent, Badge } from './styles'
import { TimelineTooltipEnclosed } from '~/components/shared'
import {
  formatAdmissionInProgressDescription,
  formatHealthProceduresTooltipDescriptionWithDate,
  preventTooltipOfBigDescriptionsIn
} from '~/utils'
import { RectangleVariantProps } from '~/constants'
import { IAdmissionRowSlots, IHealthProcedures } from '~/interfaces'
import { IconComponent } from '~/assets/icons/component'

interface CommonProps {
  position: number
  rowSize: number
  behind?: boolean
}

interface TimelineRectangleButtonOmics extends CommonProps {
  variant: 'omics'
  healthProcedures: IHealthProcedures[]
  onClick: (passages: IHealthProcedures[]) => void
}

interface TimelineRectangleButtonConduct extends CommonProps {
  variant: 'conduct' | 'diagnosis'
  healthProcedures: IAdmissionRowSlots[]
  onClick: (passages: IAdmissionRowSlots[]) => void
}

export type TimelineRectangleButtonProps = TimelineRectangleButtonOmics | TimelineRectangleButtonConduct

export function TimelineRectangleButton({
  healthProcedures,
  position,
  rowSize,
  variant,
  behind,
  onClick
}: TimelineRectangleButtonProps): JSX.Element {
  const rectangleVariant = RectangleVariantProps[variant]
  const descriptions: string[] =
    (variant === 'conduct' || variant === 'diagnosis') && healthProcedures[0].isInProgress
      ? [formatAdmissionInProgressDescription(healthProcedures)]
      : formatHealthProceduresTooltipDescriptionWithDate(healthProcedures)
  const tooltipDescriptionMultiline = preventTooltipOfBigDescriptionsIn(descriptions)

  const verifyIfHasAlert = (healthProcedures: IHealthProcedures[]): boolean => {
    return healthProcedures.some(healthProcedure => healthProcedure.isAltered)
  }

  const handleClick = () => {
    switch (variant) {
      case 'conduct':
        onClick(healthProcedures)
        return
      case 'diagnosis':
        onClick(healthProcedures)
        return
      default:
        onClick(healthProcedures)
    }
  }

  const content = () => {
    return (
      <RectangleButtonContent backgroundColor={rectangleVariant.color} behindEllipses={!!behind}>
        <div className="background" data-testid="rectangle-background" />

        {healthProcedures.length > 1 || verifyIfHasAlert(healthProcedures) ? (
          <Badge color={rectangleVariant.color} isAltered={verifyIfHasAlert(healthProcedures)}>
            {healthProcedures.length > 1 ? healthProcedures.length : '!'}
          </Badge>
        ) : null}

        <IconComponent name={rectangleVariant.iconName} />
      </RectangleButtonContent>
    )
  }

  return (
    <RectangleButton position={position} rowSize={rowSize} tooltipDisable={!!behind} onClick={handleClick}>
      {behind ? (
        content()
      ) : (
        <TimelineTooltipEnclosed description={tooltipDescriptionMultiline}>{content()}</TimelineTooltipEnclosed>
      )}
    </RectangleButton>
  )
}
